import { memo } from 'react';
import { styled } from '@mui/material/styles';
import { Box, SxProps, Theme } from '@mui/material';
import { RfNodeGroup } from '../canvas-types/nodeTypesData';
import { CANVAS_COLOR_HIGHLIGHT, HALF_NODE_GAP, NODE_WIDTH } from '../const';
import { CanvasMode, NodeEntityTypes } from '../canvas-types';
import { getObjectTypeColor, objectTypeColorNames } from '../helpers/getObjectTypeColor';
import { canvasModeToClass, CONTEXT_ZOOM_LEVEL_2_CLASS_NAME } from './contextZoomConsts';
import { IconButton } from '@sweep-io/sweep-design';
import { MoreActionsVertical } from '@sweep-io/sweep-design/dist/icons';
import { NodeProps } from '@xyflow/react';
import { TopTag } from './components/TopTag';
import { createZoomTransformNodeClass, ZOOM_TRANSFORM_CLASSES } from './getZoomTransformStyle';
import classNames from 'classnames';

const SHOW_GROUP_BOUNDARIES =
  new URLSearchParams(window.location.search).get('SHOW_GROUP_BOUNDARIES') === 'true';
const StyledGroupNode = styled(Box)({
  position: 'relative',
  borderRadius: 8,
});

const getSweepNodeBackgroundColors = (bgOpacity: string) =>
  objectTypeColorNames.reduce((acc, objectType) => {
    const color = getObjectTypeColor(objectType).connection + bgOpacity;
    return {
      ...acc,
      [`.sweep-group-node.object-type-${objectType}`]: {
        backgroundColor: color,
      },
    };
  }, {});

export const sweepNodeCanvasSx: SxProps<Theme> = {
  [`&:not(.${canvasModeToClass[CanvasMode.PREVIEW2]})`]: {
    '.sweep-group-node': {
      backgroundColor: getObjectTypeColor().connection + '0D',
    },
    ...getSweepNodeBackgroundColors('0D'),
  },
  [`&.${CONTEXT_ZOOM_LEVEL_2_CLASS_NAME}`]: {
    '.sweep-group-node': {
      backgroundColor: getObjectTypeColor().connection + '1A',
    },
    ...getSweepNodeBackgroundColors('1A'),
  },
};

const GroupNode = ({
  data: {
    hideGroupInfo,
    objectType,
    boundingBox,
    onClick,
    isGhost,
    showEditButton,
    groupId,
    simpleHighlighted,
    topTag,
    zoomTransformSuffix,
  },
  selected,
}: NodeProps<RfNodeGroup>) => {
  if (hideGroupInfo) {
    return <></>;
  }

  const { height, marginLeft, marginTop, width } = boundingBox;
  const _width = isGhost ? width + HALF_NODE_GAP + NODE_WIDTH / 2 : width;

  const maybeRenderTopTag = topTag ? <TopTag topTag={topTag} /> : null;

  const groupNode = (
    <StyledGroupNode
      onClick={(event) =>
        onClick?.({ event, entity: { type: NodeEntityTypes.Group }, id: groupId })
      }
      className={classNames('sweep-group-node', {
        [`object-type-${objectType}`]: true,
        'sweep-group-node': true,
        [ZOOM_TRANSFORM_CLASSES.NODE]: true,
        [createZoomTransformNodeClass(zoomTransformSuffix)]: true,
      })}
      sx={{
        height: height + 'px',
        marginLeft: marginLeft + 'px',
        marginTop: marginTop + 'px',
        width: _width + 'px',
        cursor: 'move',
        position: 'relative',
        '.edit-group-button': {
          opacity: 0,
        },
        '&:hover': {
          '.edit-group-button': {
            opacity: 1,
          },
        },
        ...((simpleHighlighted || selected) && {
          border: `2px solid ${CANVAS_COLOR_HIGHLIGHT}`,
        }),
      }}
    >
      {maybeRenderTopTag}
      {showEditButton && (
        <Box position="absolute" right={20} top={12} className="edit-group-button">
          <IconButton
            variant="system"
            onClick={(event) => {
              onClick?.({
                event,
                entity: { type: NodeEntityTypes.GroupEditButton },
                id: groupId,
              });
            }}
          >
            <MoreActionsVertical />
          </IconButton>
        </Box>
      )}
    </StyledGroupNode>
  );

  if (SHOW_GROUP_BOUNDARIES) {
    return <Box sx={{ border: '1px solid red' }}>{groupNode}</Box>;
  }

  return groupNode;
};

const _memoGroupNode = memo(GroupNode);

export { _memoGroupNode as GroupNode };
